import { Box, Checkbox, CheckboxGroup, FormControl, HStack, Image, Radio, RadioGroup, Spinner } from '@chakra-ui/react'
import type { SimpleItem } from 'BrokerTsLib/src/dtos'
import { Controller, useFormContext } from 'BrokerTsLib/src/rhf'
import { useEffect } from 'react'

import { useTranslationsContext } from 'BrokerTsLib/src/context/TranslationsContext'
import { useInsuranceParams } from '../context/InsuranceParamsProvider'
import { getLogo } from '../utils/companiesLogos'
import { FormLabel } from './FormLabel'

export type ICompany = {
  id: number
  name: string
}

interface CompanyProps extends ICompany {
  checked?: boolean
}

const CompanyCheckbox = ({ children }: any) => {
  return (
    <Box
      as="label"
      sx={{
        display: 'flex',
        gap: 4,
        alignItems: 'center',
        px: 4,
        py: 1,
        borderRadius: 'sm',
        cursor: 'pointer',
        _hover: {
          backgroundColor: 'gray.50',
        },
      }}
    >
      {children}
    </Box>
  )
}

export const InsuranceCompanies = ({
  companies,
  title,
  tooltip,
  isLoadingCompanies,
  propPath,
  isDisabled = false
}: {
  companies: (ICompany | SimpleItem)[],
  title?: string,
  tooltip?: string,
  isLoadingCompanies: boolean,
  propPath: string,
  isDisabled?: boolean
}) => {
  const { translate } = useTranslationsContext()
  const { control, watch, setValue } = useFormContext()
  const { insuranceParamsId: paramsId } = useInsuranceParams()!

  const selected = watch(propPath) ?? []
  const selectAll = () => selected.length === 0

  const handleSelectAll = () => {
    setValue(propPath, selectAll() ? companies.map(comp => comp.id) : [])
  }

  const companiesLength = companies?.length ?? 0
  useEffect(() => {
    if (companiesLength > 0 && !paramsId)
      handleSelectAll()
  }, [paramsId, companiesLength])

  return (
    <Box>
      <Box as="header" sx={{ display: 'flex', gap: 4, mb: 4 }}>
        <FormControl sx={{ gap: 4 }}>
          <FormLabel tooltip={tooltip}>{title ?? translate('insuranceCompanies.selectInsurersTitle', 'Vybrané pojišťovny')}</FormLabel>

          <RadioGroup
            onChange={(val) => {
              if (val === 'all')
                setValue(propPath, companies.map(comp => comp.id))
              else
                setValue(propPath, [])
            }}
            value={selected.length >= companiesLength ? 'all' : 'custom'}
          >
            <HStack align="start" gap={8} py={4}>
              <Radio value="all">{translate('insuranceCompanies.selectAll', 'Všechny dostupné')}</Radio>
              <Radio value="custom">{translate('insuranceCompanies.selectCustom', 'Vlastní výběr')}</Radio>
            </HStack>
          </RadioGroup>
        </FormControl>

        {isLoadingCompanies && <Spinner />}
      </Box>

      {selected.length < companiesLength &&
        <Controller
          name={propPath}
          control={control}
          render={({ field: { onChange, value } }) => (
            <CheckboxGroup onChange={values => onChange(values.map(val => parseInt(val.toString())))} value={value} isDisabled={isDisabled}>
              <Box as="main" sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {companies && companies.map(({ id, name }: CompanyProps) =>
                  <CompanyCheckbox key={id}>
                    <Checkbox value={id} />

                    <Image src={getLogo(id)} alt={name} title={name} width="64px" maxH="48px" />
                  </CompanyCheckbox>
                )}
              </Box>
            </CheckboxGroup>
          )
          }
        />
      }
    </Box >
  )
}
